import { gql, useMutation } from '@apollo/client';
import { ServiceLevel, serviceLevelNameToPlatformSlaId } from '@provision/service-level-utils';
import dayjs from 'dayjs';
import { useCallback } from 'react';
import { generateFieldModifiersMap } from 'src/graphql/utils';
import { useSnackbar } from 'src/utils/hooks/use-snackbar';

const PAUSE_SUBSCRIPTION_MUTATION = gql`
  mutation PauseMembership($addressId: String!, $partnerId: Int!, $pauseUntil: String!) {
    pauseSubscription(
      params: { addressId: $addressId, partnerId: $partnerId, pauseUntil: $pauseUntil }
    ) {
      success
    }
  }
`;

const UNPAUSE_SUBSCRIPTION_MUTATION = gql`
  mutation UnpauseMembership($addressId: String!, $partnerId: Int!) {
    unpauseSubscription(params: { addressId: $addressId, partnerId: $partnerId }) {
      success
    }
  }
`;

interface PauseSubscriptionMutation {
  pauseSubscription: { success: boolean };
}

interface PauseSubscriptionParams {
  addressId: string;
  partnerId: number;
  pauseUntil: string;
  /** The plan before the pause, restored in the cache by Undo. */
  planName?: string;
}

interface UnpauseSubscriptionMutation {
  unpauseSubscription: { success: boolean };
}

export const useSubscriptionPause = () => {
  const { showErrorAlert, showSuccessAlert, showActionToast } = useSnackbar();

  const [pauseMutation, { loading: pauseLoading }] = useMutation<PauseSubscriptionMutation>(
    PAUSE_SUBSCRIPTION_MUTATION,
    {
      onError: (error) => {
        console.error(error);
        showErrorAlert('Failed to pause subscription. Please try again.');
      },
    },
  );

  const [unpauseMutation, { loading: unpauseLoading }] =
    useMutation<UnpauseSubscriptionMutation>(UNPAUSE_SUBSCRIPTION_MUTATION, {
      onError: (error) => {
        console.error(error);
        showErrorAlert('Failed to unpause subscription. Please try again.');
      },
    });

  const unpauseSubscription = useCallback(
    (addressId: string, partnerId: number, planName?: string) => {
      return unpauseMutation({
        variables: { addressId, partnerId },
        onCompleted: () => {
          showSuccessAlert('Subscription unpaused successfully');
        },
        update: (cache) => {
          if (planName) {
            const id = cache.identify({ __typename: 'Address', id: addressId });
            if (id) {
              cache.modify({
                id,
                fields: generateFieldModifiersMap({
                  clientActivatedMembership: planName,
                  platformSlaId: serviceLevelNameToPlatformSlaId(planName),
                }),
              });
            }
          }
          cache.evict({
            id: 'ROOT_QUERY',
            fieldName: 'getStripeSubscriptionDetails',
          });
        },
      });
    },
    [unpauseMutation, showSuccessAlert],
  );

  const pauseSubscription = useCallback(
    ({ addressId, partnerId, pauseUntil, planName }: PauseSubscriptionParams) => {
      return pauseMutation({
        variables: { addressId, partnerId, pauseUntil },
        onCompleted: () => {
          showActionToast(`Subscription paused until ${dayjs(pauseUntil).format('MMM D')}`, {
            onUndo: () => void unpauseSubscription(addressId, partnerId, planName),
          });
        },
        update: (cache) => {
          const id = cache.identify({ __typename: 'Address', id: addressId });
          if (id) {
            cache.modify({
              id,
              fields: generateFieldModifiersMap({
                clientActivatedMembership: ServiceLevel.NonMember,
                platformSlaId: null,
              }),
            });
          }
          cache.evict({
            id: 'ROOT_QUERY',
            fieldName: 'getStripeSubscriptionDetails',
          });
        },
      });
    },
    [pauseMutation, showActionToast, unpauseSubscription],
  );

  return {
    pauseSubscription,
    unpauseSubscription,
    loading: pauseLoading || unpauseLoading,
  };
};
